import type { LearnTopic } from "../topic-types";

/**
 * Trauma Bonds.
 *
 * A flagship module on how attachment can form through cycles of harm and
 * relief, and what a gentle, self-led path toward unwinding it can look like.
 */
export const TRAUMA_BONDS_TOPIC: LearnTopic = {
  slug: "trauma-bonds",
  emoji: "🔗",
  title: "Trauma Bonds",
  tagline: "Understanding attachment formed through harm",
  theme: "peach",
  seoDescription:
    "Understand what a trauma bond is, how it can develop through cycles of hurt and relief, and gentle, trauma-informed ways to begin rebuilding safety, boundaries and identity. Guides, reflections and worksheets from Recovery Trauma™.",
  category: "bonds-boundaries",
  cardBlurb: "Why some bonds feel impossible to let go of, and what can help.",
  isNew: true,
  intro: [
    "A trauma bond is a strong emotional attachment that can form in a relationship where harm and comfort come from the same source. It is not a sign of weakness, poor judgement or love of being mistreated — it is a recognisable pattern that can happen to anyone, often without them realising it is happening.",
    "This module explores how trauma bonds can develop, why they can be so hard to leave even when a relationship is clearly hurting you, and what a slow, self-paced path toward separating your identity from the bond can involve.",
    "You do not need to have language for everything that happened to you, or to be certain a bond fits your experience, in order to find some of this useful. Take only what is helpful, and leave the rest.",
  ],
  closing:
    "Trauma bonds are a pattern to understand, not a character flaw to fix. Whatever you feel about the relationship you're thinking of — love, anger, grief, longing, relief, all at once — those feelings can make complete sense given what your nervous system went through. Healing tends to happen gradually, with support, and in your own time.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What a trauma bond is, and how these attachments can form.",
      intro:
        "Plain-English education first. Understanding the pattern often reduces the shame that can build up around it.",
      resources: [
        {
          slug: "what-is-a-trauma-bond",
          kind: "learn",
          title: "What is a trauma bond?",
          summary: "The basic idea: attachment strengthened by cycles of harm and relief.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "A trauma bond describes a powerful emotional attachment that can develop between two people where one causes harm — through cruelty, neglect, fear, control or unpredictability — and also provides comfort, affection or relief from that same harm. Over time, the nervous system can come to associate this person with both danger and safety, which creates an attachment that feels far stronger, and far harder to leave, than an ordinary bond.",
            },
            {
              type: "prose",
              text: "The term is most often used to describe romantic or intimate partner relationships, but the same pattern can appear with a parent, a family member, a friend or within a group. It can exist alongside real love, real history and real good moments — a trauma bond is not the same as saying a relationship was 'all bad'.",
            },
            {
              type: "list",
              title: "Some ideas people find helpful",
              items: [
                "A trauma bond is a nervous-system pattern, not proof of weak character or bad judgement",
                "It can form even in someone who is otherwise assertive, insightful or successful elsewhere in life",
                "The strength of the bond is not a measure of how much the relationship was worth",
                "Recognising a trauma bond is not the same as being told what to do about the relationship",
              ],
            },
            {
              type: "note",
              text: "If you are currently in a relationship where you feel frightened, controlled or unsafe, you deserve support. Our Crisis Resources page has options for immediate help, including UK domestic abuse services.",
            },
          ],
        },
        {
          slug: "how-trauma-bonds-develop",
          kind: "learn",
          title: "How trauma bonds can develop",
          summary: "The conditions that tend to make this kind of bond more likely.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Trauma bonds rarely form from a single event. They tend to build gradually, often through a slow shift in what feels normal, alongside an imbalance of power that makes leaving, questioning or resisting feel riskier over time.",
            },
            {
              type: "list",
              title: "Conditions that can make a bond more likely",
              items: [
                "An imbalance of power, where one person controls resources, decisions, affection or information",
                "Isolation from other relationships, whether gradual or sudden",
                "Unpredictability — you cannot reliably predict which version of the person you will get",
                "Intermittent warmth or affection that arrives just often enough to keep hope alive",
                "A gradual erosion of your sense of what is reasonable, described sometimes as a shifting baseline",
                "Early-life experiences of love being mixed with fear, which can make this pattern feel familiar rather than alarming",
              ],
            },
            {
              type: "prose",
              text: "None of these conditions make the harm your fault. They describe circumstances that can happen to anyone, and that make an attachment harder to see clearly from the inside while it is happening.",
            },
          ],
        },
        {
          slug: "intermittent-reinforcement",
          kind: "learn",
          title: "Intermittent reinforcement",
          summary: "Why unpredictable reward can create the strongest attachments of all.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Intermittent reinforcement is a well-studied psychological principle: rewards that arrive unpredictably create stronger, more persistent behaviour than rewards that arrive every time. This is part of why fruit machines are compelling and why an on-off relationship can feel more gripping than a steady, reliable one.",
            },
            {
              type: "prose",
              text: "In a relationship, this can look like affection, apology or attention that only arrives sometimes — often just after a period of hurt or distance. The nervous system can begin to chase that relief the way it might chase any unpredictable reward, searching for what it needs to do to bring the good version back.",
            },
            {
              type: "flow",
              title: "A pattern some people recognise",
              items: [
                "Tension builds",
                "Something painful happens",
                "A period of distance, silence or fear",
                "An apology, affection, or return of warmth",
                "Relief — and hope that this time it will last",
                "Tension slowly begins to build again",
              ],
            },
            {
              type: "note",
              text: "Recognising this pattern in a relationship is educational, not a verdict on the other person's character or intentions. It describes a dynamic, and dynamics can be understood without needing to diagnose anyone.",
            },
          ],
        },
        {
          slug: "cycle-of-hurt-and-relief",
          kind: "learn",
          title: "The cycle of hurt and relief",
          summary: "How pain and comfort from the same source become entangled.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When the same person causes both the pain and the relief from that pain, the brain can begin to associate them together rather than seeing them as separate events. The relief can start to feel disproportionately powerful — almost euphoric — precisely because of how much distress came before it.",
            },
            {
              type: "list",
              title: "This cycle can create",
              items: [
                "A feeling of being emotionally 'hooked', even when you can see the harm clearly",
                "Confusion about your own feelings — love and fear can be present at once",
                "A sense that the good moments 'prove' the relationship is really fine underneath",
                "Difficulty trusting your own judgement about what is happening",
              ],
            },
            {
              type: "prose",
              text: "None of this means you are being irrational. Your nervous system is doing exactly what nervous systems are built to do with unpredictable danger and unpredictable comfort — it is trying to keep you safe using the tools it has.",
            },
          ],
        },
        {
          slug: "leaving-and-the-bond",
          kind: "learn",
          title: "Why leaving may not immediately end the attachment",
          summary: "Physical distance and emotional attachment can move at different speeds.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Ending contact with someone, or ending a relationship, does not automatically end a trauma bond. The attachment lives in the nervous system, in memory and in habit, and it can take considerably longer to settle than the practical steps of leaving take to complete.",
            },
            {
              type: "list",
              title: "It can be common, after leaving, to experience",
              items: [
                "Grief that feels disproportionate to how the relationship actually was",
                "Longing for contact, even knowing the relationship was harmful",
                "Missing the good moments intensely, sometimes more than expected",
                "Doubting the decision to leave, repeatedly and painfully",
                "Feeling worse for a while before feeling better",
              ],
            },
            {
              type: "note",
              text: "This is a recognised part of the pattern, not evidence that leaving was the wrong choice or that you 'aren't over it' quickly enough. Unwinding a trauma bond tends to take time.",
            },
          ],
        },
        {
          slug: "idealisation",
          kind: "learn",
          title: "Idealisation and remembering the good moments",
          summary: "Why the mind can hold on tightly to the best version of someone.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "It is common for memory to gravitate toward the warmest, most connected moments of a difficult relationship, especially in the early stages of separation. This isn't dishonesty or denial — vivid emotional memories are often laid down more strongly than ordinary ones, and moments of relief after fear can feel especially intense.",
            },
            {
              type: "list",
              title: "Ways idealisation can show up",
              items: [
                "Replaying the best conversations or moments on a loop",
                "Comparing new people or situations unfavourably to an idealised memory",
                "Minimising or 'explaining away' the harder parts when they come to mind",
                "Believing that if you had just done something differently, the good version would have stayed",
              ],
            },
            {
              type: "prose",
              text: "A helpful, gentle practice is holding the whole picture rather than only the highlights — not to erase the good memories, but to place them alongside the rest of what happened, so neither part gets to speak alone.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise",
      blurb: "Noticing the shape of the pattern in your own experience.",
      intro:
        "This section moves gently from reading to noticing. Nothing here needs a definite answer — curiosity is enough.",
      resources: [
        {
          slug: "cravings-to-reconnect",
          kind: "learn",
          title: "Cravings and urges to reconnect",
          summary: "Understanding the pull to message, call or return.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "The urge to reach out to someone who has hurt you can feel physical — a restlessness, an ache, a pull that has little to do with logic. This is sometimes compared to withdrawal, and while that comparison isn't a diagnosis, many people find it captures the intensity accurately.",
            },
            {
              type: "list",
              title: "Urges to reconnect often spike around",
              items: [
                "Anniversaries, birthdays or shared dates",
                "Being tired, unwell, or already low",
                "Seeing something that reminds you of a good moment",
                "Loneliness, especially late at night",
                "Success or good news you instinctively want to share",
              ],
            },
            {
              type: "prose",
              text: "An urge is a wave — it tends to rise, peak and pass, whether or not you act on it. Recognising the pattern doesn't make the urge disappear immediately, but it can make it feel less like an emergency and more like something you can ride out.",
            },
          ],
        },
        {
          slug: "the-cycle-i-recognise-reflect",
          kind: "reflect",
          title: "The cycle I recognise",
          summary: "Naming the shape of the pattern, in your own words and pace.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "You don't need to describe every detail of a relationship to notice a pattern. This reflection is about the shape of the cycle, not a full account of what happened.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "What does the 'good' phase of this cycle usually look or feel like?",
                "What tends to happen just before things turn difficult?",
                "What does the difficult phase look like for you — distance, criticism, fear, silence, something else?",
                "What usually brings the 'good' phase back?",
                "How does hope show up for you during the hard phase?",
                "What have you told yourself to make sense of the cycle repeating?",
              ],
            },
            {
              type: "note",
              text: "If writing this brings up thoughts of harming yourself, or you feel unsafe right now, please see our Self-Harm Support and Crisis Resources pages — support is available.",
            },
          ],
        },
        {
          slug: "signs-worth-noticing",
          kind: "learn",
          title: "Signs some people notice",
          summary: "A non-exhaustive list, offered for reflection rather than diagnosis.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "You might recognise some of the following",
              items: [
                "Making excuses for someone's behaviour to others, or to yourself",
                "Feeling responsible for managing someone else's moods",
                "A sense of relief that feels almost euphoric after conflict resolves",
                "Difficulty imagining life without the person, even when the relationship is painful",
                "Loss of contact with friends, family or interests over time",
                "Returning to contact after deciding, more than once, to stay away",
                "Feeling like 'you' when you're with them, in a way that frightens you slightly",
              ],
            },
            {
              type: "note",
              text: "This list is educational, not a checklist for scoring yourself or your relationship. Recognising even one item doesn't mean anything is 'wrong' with you — it means you're paying attention.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "Gentle, practical steps for boundaries, contact and grief.",
      intro:
        "These practices are optional, and you're always allowed to stop, pause, or come back another time.",
      resources: [
        {
          slug: "breaking-contact-patterns",
          kind: "practice",
          title: "Breaking contact patterns",
          summary: "Practical, compassionate steps for reducing or ending contact.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Reducing or ending contact with someone you're bonded to is rarely a single clean decision — it's often a series of small, imperfect steps, sometimes with setbacks along the way. Setbacks don't erase progress; they're a normal part of unwinding a strong attachment.",
            },
            {
              type: "steps",
              title: "Ideas that some people find helpful",
              items: [
                { label: "Decide your baseline in advance", note: "What contact, if any, are you choosing — no contact, low contact, or structured contact for practical reasons?" },
                { label: "Reduce access points", note: "Muting, blocking or archiving can lower the number of moments where a decision has to be remade." },
                { label: "Tell one trusted person your plan", note: "So there's somewhere to turn to instead of reaching out to them in a hard moment." },
                { label: "Prepare for urges, not just the decision", note: "Have something ready for the moment the urge hits — a person to call, a grounding tool, a place to go." },
                { label: "Expect it to feel harder before it feels easier", note: "This is a known part of the process, not a sign you're doing it wrong." },
                { label: "Be honest with yourself about slips, without punishing yourself", note: "A slip is information, not proof of failure." },
              ],
            },
            {
              type: "note",
              text: "If contact involves any risk to your safety — including shared children, finances, housing or fear of retaliation — please see our Crisis Resources page for specialist domestic abuse support. You know your situation best, and this page cannot tell you whether to stay in contact or not.",
            },
          ],
        },
        {
          slug: "urge-to-reconnect-worksheet",
          kind: "worksheet",
          title: "Urge to reconnect — what helps",
          summary: "A printable page to fill in ahead of time, ready for the next urge.",
          minutes: 8,
          worksheetFields: [
            { label: "What tends to trigger my urge to reconnect", lines: 3 },
            { label: "What the urge usually feels like in my body", lines: 2 },
            { label: "What I actually want in that moment (comfort? certainty? to be missed?)", lines: 3 },
            { label: "Three things I can do instead of reaching out", lines: 3 },
            { label: "Someone I can contact instead", lines: 1 },
            { label: "A reminder to myself for the hard moments", lines: 2 },
            { label: "How I'll feel about myself tomorrow if I don't act on the urge tonight", lines: 2 },
          ],
        },
        {
          slug: "boundaries",
          kind: "learn",
          title: "Boundaries after a trauma bond",
          summary: "Rebuilding limits when they may have eroded over time.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "In a trauma-bonded relationship, boundaries often erode gradually — not because you failed to have them, but because holding them became costly or unsafe. Rebuilding boundaries afterwards, with this person or with others, is frequently one of the slower parts of recovery.",
            },
            {
              type: "list",
              title: "Boundaries worth considering, at your own pace",
              items: [
                "What information about your life you choose to share, and with whom",
                "How much contact, if any, feels manageable right now",
                "What you will and won't discuss if contact is unavoidable",
                "Limits with people who still speak positively about the person who hurt you",
                "Boundaries with yourself — such as a pause before responding to any message",
              ],
            },
            {
              type: "prose",
              text: "Boundaries are not about punishing anyone. They're a way of protecting your own capacity to heal. For a deeper look at building and holding boundaries, see our Boundaries module.",
            },
          ],
        },
        {
          slug: "grieving-the-relationship",
          kind: "learn",
          title: "Grieving the relationship",
          summary: "Making room for loss, even when the relationship was harmful.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Grief after a trauma bond can be complicated — you may be grieving the relationship as it actually was, the relationship you hoped it could become, and the version of yourself who believed in it. All of these losses can be real at once, and grief for a harmful relationship deserves as much care as any other grief.",
            },
            {
              type: "list",
              title: "This grief can include",
              items: [
                "Mourning good moments that were also real",
                "Anger at the harm, alongside sadness at the loss",
                "Grieving time, plans, or a future you imagined",
                "Grieving the version of you who didn't yet know what you know now",
              ],
            },
            {
              type: "quote",
              text: "You can miss something and still know it wasn't good for you. Both things can be true." ,
            },
            {
              type: "prose",
              text: "Our Grief & Trauma module explores grief in more depth, including grief that doesn't fit neatly into a tidy story.",
            },
          ],
        },
        {
          slug: "rebuilding-identity",
          kind: "practice",
          title: "Rebuilding identity beyond the bond",
          summary: "Gently reconnecting with who you are outside the relationship.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Trauma bonds can take up a great deal of emotional and mental space, sometimes crowding out interests, friendships, opinions and routines that once felt like 'you'. Rebuilding identity afterwards is rarely instant — it tends to happen through small, repeated acts of reconnecting with yourself.",
            },
            {
              type: "steps",
              title: "Small ways back to yourself",
              items: [
                { label: "Revisit an old interest, even briefly", note: "Something you enjoyed before or during the relationship, without pressure to love it instantly again." },
                { label: "Reconnect with one relationship at a time", note: "A friend or family member you may have drifted from — at whatever pace feels comfortable." },
                { label: "Notice small preferences", note: "What you want for dinner, what you want to watch — rebuilding the habit of having a say." },
                { label: "Try something new, with no pressure to be good at it", note: "New experiences can help build a sense of self that isn't tied to the old relationship." },
                { label: "Keep a simple record of who you're becoming", note: "A note, a journal entry, a photo — small evidence for the days it's hard to see progress." },
              ],
            },
          ],
        },
        {
          slug: "rebuilding-my-identity-worksheet",
          kind: "worksheet",
          title: "Rebuilding my identity",
          summary: "A printable worksheet for reconnecting with yourself, piece by piece.",
          minutes: 10,
          worksheetFields: [
            { label: "Things I enjoyed before this relationship began", lines: 3 },
            { label: "Parts of myself I felt I had to shrink or hide", lines: 3 },
            { label: "People I'd like to reconnect with, even a little", lines: 3 },
            { label: "One small thing that is just for me, this week", lines: 2 },
            { label: "A quality I have that this relationship didn't get to see", lines: 2 },
            { label: "What I would tell a friend going through this", lines: 3 },
            { label: "One sentence about who I'm becoming", lines: 2 },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Journal prompts for understanding patterns and healthier ground.",
      intro:
        "Write freely, without needing to reach a conclusion. These prompts are here to support reflection, not to produce a verdict.",
      resources: [
        {
          slug: "recognising-healthier-patterns",
          kind: "learn",
          title: "Recognising healthier relationship patterns",
          summary: "What steadier, safer connection can tend to feel like.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "After a trauma bond, healthier relationships can initially feel underwhelming, even boring — because the nervous system has learned to associate intensity with connection. Steadiness can take some getting used to, and that adjustment period is normal rather than a sign something is missing.",
            },
            {
              type: "list",
              title: "Some markers people find helpful to notice",
              items: [
                "Consistency between someone's words and their actions over time",
                "Feeling like yourself around them, rather than managing them",
                "Disagreements that resolve without fear, threats or silence",
                "Being able to have needs and boundaries without punishment",
                "A general sense of predictability rather than walking on eggshells",
                "Calm feeling like relief, not like something is wrong",
              ],
            },
            {
              type: "note",
              text: "This isn't a test to apply to people early on, and it isn't about becoming suspicious of everyone. It's a gentle reference point as your sense of 'normal' recalibrates.",
            },
            {
              type: "prose",
              text: "If you'd like a plain-English public introduction to this topic to share with someone else, or to read again outside this programme, Recovery Trauma™ also publishes articles on the blog at /blog/category/trauma-bonds.",
            },
          ],
        },
        {
          slug: "reflect-on-the-pattern",
          kind: "reflect",
          title: "Reflecting on the wider pattern",
          summary: "Looking at this bond in the context of your life, gently.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "Where else in my life have I felt this particular mix of longing and fear?",
                "What did love or care look like in my early life?",
                "What did I learn, growing up, about what I had to do to be loved or kept safe?",
                "What would I want a younger version of me to know about this?",
                "What is one belief about relationships I'd like to gently question?",
                "What would 'enough' feel like, in a relationship — not perfect, just enough?",
              ],
            },
            {
              type: "note",
              text: "Some of these questions can bring up difficult memories. It's okay to answer only some of them, or to come back another day.",
            },
          ],
        },
        {
          slug: "letter-you-dont-send",
          kind: "reflect",
          title: "A letter you don't send",
          summary: "An optional exercise for expressing what's unsaid, safely.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "Some people find it helpful to write everything they wish they could say — to the person, or to the relationship itself — without ever sending it. This is entirely optional, and there's no right way to do it.",
            },
            {
              type: "prompts",
              items: [
                "What do you wish they understood about what this cost you?",
                "What do you wish you'd said, at the time or since?",
                "What are you grateful you learned, even if the learning was painful?",
                "What are you ready to put down, even if only for today?",
              ],
            },
            {
              type: "note",
              text: "If it feels safer, you can destroy the letter afterwards — burning, tearing or deleting it can be part of the exercise for some people.",
            },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Boundaries", to: "/learn/boundaries" },
    { label: "Attachment Styles", to: "/learn/attachment-styles" },
    { label: "Shame-Based Trauma", to: "/learn/shame-based-trauma" },
    { label: "Journal", to: "/journal" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
};
